import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ArrowRight } from 'lucide-react';



const MFAVerification = ({ onLogin }) => {
  const [totpCode, setTotpCode] = useState(Array(6).fill('')); // Initialize as an array of 6 empty strings
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const username = location.state?.username;
  const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const handleChange = (index, value) => {
    if (/^\d*$/.test(value)) {
      // Handle pasting multiple digits
      if (value.length > 1) {
        const digits = value.split('').slice(0, 6);
        const newTotpCode = [...totpCode];
        digits.forEach((digit, i) => {
          if (i < 6) newTotpCode[i] = digit;
        });
        setTotpCode(newTotpCode);
        
        // If we have all 6 digits, submit the code
        if (digits.length === 6) {
          submitCode(newTotpCode);
        } else {
          // Focus the next empty input
          const nextEmptyIndex = digits.length;
          if (nextEmptyIndex < 6) {
            inputRefs[nextEmptyIndex].current.focus();
          }
        }
      } else {
        // Handle single digit input (existing logic)
        const newTotpCode = [...totpCode];
        newTotpCode[index] = value;
        setTotpCode(newTotpCode);

        if (value && index < 5) {
          inputRefs[index + 1].current.focus();
        } else if (value && index === 5) {
          const completeCode = [...newTotpCode.slice(0, 5), value];
          if (completeCode.every(digit => digit !== '')) {
            submitCode(completeCode);
          }
        }
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !totpCode[index] && index > 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const submitCode = async (code) => {
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch('https://plansync-backend-234288438538.us-central1.run.app/api/auth/verify-totp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: username,
          totp_code: code.join(''),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'MFA verification failed');
      }

      // Store the authentication data
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('userid', data.user_data.userid);
      localStorage.setItem('username', data.user_data.username);
      
      // Store company information - default to 'Adidas' for now
      const userCompany = data.user_data.company || 'Adidas';
      localStorage.setItem('userCompany', userCompany);
      
      // Check if user is a customer (you can adjust this logic based on your user data structure)
      // For now, we'll assume all users are customers unless they have an 'isAdmin' flag
      const isCustomer = !data.user_data.isAdmin;
      localStorage.setItem('isCustomerView', isCustomer.toString());
      
      // Force a page reload to trigger the authentication check
      window.location.href = '/spreadsheets/Plan%20Touchpoints';
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitCode(totpCode);
  };

  // Add a paste handler for the input fields
  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    handleChange(index, pastedData);
  };

  useEffect(() => {
    inputRefs[0].current.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-lg shadow-gray-400/20 p-8 w-full max-w-md border border-slate-200/50"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-sky-400 to-blue-600 rounded-2xl mx-auto mb-6 flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-sky-600 bg-clip-text text-transparent mb-2">Two-Factor Authentication</h2>
          <p className="text-gray-500 text-sm">Enter the 6-digit code from your Google Authenticator app</p>
        </motion.div>
        
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6 rounded-xl flex items-start space-x-2"
              role="alert"
            >
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-between gap-2">
            {totpCode.map((digit, index) => (
              <motion.input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(e, index)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-14 h-14 text-center text-2xl font-semibold bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 transition-all duration-200"
                autoComplete="off"
              />
            ))}
          </div>
          <div className="pt-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className={`w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-3.5 rounded-xl flex items-center justify-center space-x-2 font-medium shadow-lg shadow-sky-500/25 transition-all duration-200 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl hover:shadow-sky-500/30'
              }`}
              disabled={isLoading || totpCode.some(digit => digit === '')}
            >
              {isLoading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                  />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <span>Verify Code</span>
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default MFAVerification;
